import React, { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../context/AppContext";
import AdminNavbar from "../components/AdminNavbar";
import toast from "react-hot-toast";

const STATUS_CONFIG = {
  pending: {
    label: "Pending",
    color: "#f59e0b",
    bg: "#fffbeb",
    border: "#fde68a",
    icon: "🕐",
  },
  confirmed: {
    label: "Confirmed",
    color: "#3b82f6",
    bg: "#eff6ff",
    border: "#bfdbfe",
    icon: "✅",
  },
  preparing: {
    label: "Preparing",
    color: "#8b5cf6",
    bg: "#f5f3ff",
    border: "#ddd6fe",
    icon: "👨‍🍳",
  },
  shipped: {
    label: "Shipped",
    color: "#f97316",
    bg: "#fff7ed",
    border: "#fed7aa",
    icon: "📦",
  },
  out_for_delivery: {
    label: "Out for Delivery",
    color: "#06b6d4",
    bg: "#ecfeff",
    border: "#a5f3fc",
    icon: "🚴",
  },
  delivered: {
    label: "Delivered",
    color: "#16a34a",
    bg: "#f0fdf4",
    border: "#bbf7d0",
    icon: "🎉",
  },
  cancelled: {
    label: "Cancelled",
    color: "#ef4444",
    bg: "#fef2f2",
    border: "#fecaca",
    icon: "✕",
  },
};

const STATUS_FLOW = [
  "pending",
  "confirmed",
  "preparing",
  "shipped",
  "out_for_delivery",
  "delivered",
];

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [adminNote, setAdminNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [removedItems, setRemovedItems] = useState(new Set());
  const [savingItems, setSavingItems] = useState(false);

  const pageRef = useRef(page);
  const filterRef = useRef(filterStatus);
  const searchRef = useRef(search);
  useEffect(() => {
    pageRef.current = page;
  }, [page]);
  useEffect(() => {
    filterRef.current = filterStatus;
  }, [filterStatus]);
  useEffect(() => {
    searchRef.current = search;
  }, [search]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pageRef.current, limit: 15 });
      if (filterRef.current !== "all")
        params.append("status", filterRef.current);
      if (searchRef.current) params.append("search", searchRef.current);
      const res = await api.get(`/admin/orders?${params}`);
      setOrders(res.data.orders);
      setTotalPages(res.data.pages);
      setTotalOrders(res.data.total || 0);
    } catch {
      toast.error("Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    fetchOrders();
  }, [page, filterStatus, fetchOrders]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (pageRef.current !== 1) setPage(1);
      else fetchOrders();
    }, 400);
    return () => clearTimeout(t);
  }, [search, fetchOrders]);

  const handleUpdateStatus = async (orderId, newStatus) => {
    setUpdating(true);
    try {
      await api.patch(`/admin/orders/${orderId}/status`, { status: newStatus });
      toast.success(`Updated to ${newStatus.replace(/_/g, " ")}`);
      fetchOrders();
      if (selectedOrder?._id === orderId) {
        const res = await api.get(`/admin/orders/${orderId}`);
        setSelectedOrder(res.data);
      }
    } catch {
      toast.error("Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  const handleSaveNote = async () => {
    if (!selectedOrder) return;
    setSavingNote(true);
    try {
      await api.patch(`/admin/orders/${selectedOrder._id}/admin-note`, {
        adminNote,
      });
      toast.success("Note saved — customer can now see it");
      // update local state so the modal reflects the saved note
      setSelectedOrder((prev) => ({ ...prev, adminNote }));
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save note");
    } finally {
      setSavingNote(false);
    }
  };

  const handleSaveItems = async () => {
    if (!selectedOrder) return;
    setSavingItems(true);
    try {
      const res = await api.patch(`/admin/orders/${selectedOrder._id}/items`, {
        removedItemIndexes: [...removedItems],
      });
      toast.success("Order updated — bill recalculated");
      setSelectedOrder(res.data.order);
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update items");
    } finally {
      setSavingItems(false);
    }
  };

  const getNextStatuses = (curr) => {
    if (curr === "cancelled" || curr === "delivered") return [];
    const idx = STATUS_FLOW.indexOf(curr);
    if (idx === -1) return [];
    const next = [];
    if (idx + 1 < STATUS_FLOW.length) next.push(STATUS_FLOW[idx + 1]);
    next.push("cancelled");
    return next;
  };

  return (
    <div
      className="min-h-screen"
      style={{ background: "linear-gradient(135deg,#f0f9f0,#e8f5e9)" }}
    >
      <AdminNavbar />

      <div className="page-gradient py-6">
        <div className="max-w-screen-xl mx-auto px-4">
          <h1 className="text-3xl font-extrabold text-white">
            Order Management
          </h1>
          <p className="text-white/80 text-sm">
            Manage and update customer orders
          </p>
        </div>
      </div>

      <div className="max-w-screen-xl mx-auto px-4 py-6">
        {/* Filters */}
        <div className="glass-card p-4 mb-5">
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                🔍
              </span>
              <input
                type="text"
                placeholder="Search by order ID, customer name or phone…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="input-glass pl-8 text-sm"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setPage(1);
              }}
              className="input-glass text-sm"
            >
              <option value="all">All Orders</option>
              {Object.entries(STATUS_CONFIG).map(([key, { label }]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
            <button onClick={fetchOrders} className="btn-outline-green text-sm">
              ↻ Refresh
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="admin-table-wrap overflow-hidden">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="w-10 h-10 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-gray-400 font-semibold">
                Loading orders…
              </p>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-5xl mb-3">📭</div>
              <p className="text-gray-400 font-semibold">No orders found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="admin-table">
                  <thead className="admin-thead">
                    <tr>
                      {[
                        "Order ID",
                        "Customer",
                        "Items",
                        "Total",
                        "Payment",
                        "Status",
                        "Date",
                        "Action",
                      ].map((h) => (
                        <th key={h} className="admin-th">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order, idx) => {
                      const cfg =
                        STATUS_CONFIG[order.orderStatus] ||
                        STATUS_CONFIG.pending;
                      const cancelEntry = order.statusHistory?.find(
                        (h) => h.status === "cancelled",
                      );
                      return (
                        <tr
                          key={order._id}
                          className="admin-tr"
                          style={{ animationDelay: `${idx * 0.04}s` }}
                        >
                          <td className="admin-td">
                            <span className="font-black text-green-600">
                              #{order.orderId}
                            </span>
                          </td>
                          <td className="admin-td">
                            <p className="font-semibold text-gray-800">
                              {order.customerDetails?.name || "—"}
                            </p>
                            <p className="text-xs text-gray-400 font-mono">
                              {order.customerDetails?.phone}
                            </p>
                          </td>
                          <td className="admin-td">
                            <span className="status-chip bg-blue-50 text-blue-700 border border-blue-100">
                              {order.items?.length} item
                              {order.items?.length !== 1 ? "s" : ""}
                            </span>
                          </td>
                          <td className="admin-td">
                            <span className="font-black text-green-600 text-base">
                              ₹{order.total}
                            </span>
                          </td>
                          <td className="admin-td">
                            <span className="status-chip bg-gray-100 text-gray-600 border border-gray-200 font-mono text-xs">
                              {order.paymentMethod?.toUpperCase()}
                            </span>
                          </td>
                          <td className="admin-td">
                            <div>
                              <span
                                className="status-chip text-white font-bold text-xs"
                                style={{ background: cfg.color }}
                              >
                                {cfg.icon} {cfg.label}
                              </span>
                              {/* show cancel reason inline in table */}
                              {order.orderStatus === "cancelled" &&
                                cancelEntry?.note &&
                                cancelEntry.note !==
                                  "Cancelled by customer" && (
                                  <p
                                    className="text-xs text-red-400 mt-1 max-w-[140px] truncate"
                                    title={cancelEntry.note}
                                  >
                                    "{cancelEntry.note}"
                                  </p>
                                )}
                            </div>
                          </td>
                          <td className="admin-td text-xs text-gray-400 whitespace-nowrap">
                            {fmtDate(order.createdAt)}
                          </td>
                          <td className="admin-td">
                            <button
                              onClick={() => {
                                setSelectedOrder(order);
                                setShowHistory(false);
                                setAdminNote(order.adminNote || "");
                                setRemovedItems(
                                  new Set(
                                    (order.items || [])
                                      .map((it, i) => (it.removed ? i : -1))
                                      .filter((i) => i >= 0),
                                  ),
                                );
                              }}
                              className="text-xs font-bold px-3 py-1.5 rounded-full transition-all hover:scale-105"
                              style={{
                                background:
                                  "linear-gradient(135deg,#2D8B4E,#4CAF72)",
                                color: "white",
                                boxShadow: "0 2px 8px rgba(45,139,78,0.3)",
                              }}
                            >
                              Manage
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 &&
                (() => {
                  const LIMIT = 15;
                  const from = (page - 1) * LIMIT + 1;
                  const to = Math.min(page * LIMIT, totalOrders);

                  // Build windowed page list: always show first, last, current ±2, with … gaps
                  const pageNums = [];
                  for (let p = 1; p <= totalPages; p++) {
                    if (
                      p === 1 ||
                      p === totalPages ||
                      (p >= page - 2 && p <= page + 2)
                    ) {
                      pageNums.push(p);
                    }
                  }
                  // Insert ellipsis markers
                  const withGaps = [];
                  pageNums.forEach((p, i) => {
                    if (i > 0 && p - pageNums[i - 1] > 1) withGaps.push("…");
                    withGaps.push(p);
                  });

                  return (
                    <div
                      style={{
                        padding: "12px 16px",
                        borderTop: "1px solid #f0f0f0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 10,
                      }}
                    >
                      {/* info */}
                      <span
                        style={{
                          fontSize: 12,
                          color: "#9ca3af",
                          fontWeight: 500,
                        }}
                      >
                        Showing{" "}
                        <strong style={{ color: "#374151" }}>
                          {from}–{to}
                        </strong>{" "}
                        of{" "}
                        <strong style={{ color: "#374151" }}>
                          {totalOrders}
                        </strong>{" "}
                        orders
                      </span>

                      {/* controls */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        {/* Prev */}
                        <button
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                          disabled={page === 1}
                          style={{
                            height: 32,
                            padding: "0 12px",
                            borderRadius: 8,
                            border: "1.5px solid #e5e7eb",
                            background: "#fff",
                            fontSize: 12,
                            fontWeight: 700,
                            color: page === 1 ? "#d1d5db" : "#374151",
                            cursor: page === 1 ? "not-allowed" : "pointer",
                            transition: "all 0.15s",
                          }}
                        >
                          ← Prev
                        </button>

                        {/* Page numbers */}
                        {withGaps.map((p, i) =>
                          p === "…" ? (
                            <span
                              key={`gap-${i}`}
                              style={{
                                width: 32,
                                textAlign: "center",
                                fontSize: 13,
                                color: "#9ca3af",
                              }}
                            >
                              …
                            </span>
                          ) : (
                            <button
                              key={p}
                              onClick={() => setPage(p)}
                              style={{
                                width: 32,
                                height: 32,
                                borderRadius: 8,
                                border: "1.5px solid",
                                borderColor: p === page ? "#2D8B4E" : "#e5e7eb",
                                background: p === page ? "#2D8B4E" : "#fff",
                                color: p === page ? "#fff" : "#374151",
                                fontSize: 12,
                                fontWeight: p === page ? 800 : 500,
                                cursor: "pointer",
                                transition: "all 0.15s",
                              }}
                            >
                              {p}
                            </button>
                          ),
                        )}

                        {/* Next */}
                        <button
                          onClick={() =>
                            setPage((p) => Math.min(totalPages, p + 1))
                          }
                          disabled={page === totalPages}
                          style={{
                            height: 32,
                            padding: "0 12px",
                            borderRadius: 8,
                            border: "1.5px solid #e5e7eb",
                            background: "#fff",
                            fontSize: 12,
                            fontWeight: 700,
                            color: page === totalPages ? "#d1d5db" : "#374151",
                            cursor:
                              page === totalPages ? "not-allowed" : "pointer",
                            transition: "all 0.15s",
                          }}
                        >
                          Next →
                        </button>
                      </div>
                    </div>
                  );
                })()}
            </>
          )}
        </div>
      </div>

      {/* ── Order Detail Modal ── */}
      {selectedOrder &&
        (() => {
          const cfg =
            STATUS_CONFIG[selectedOrder.orderStatus] || STATUS_CONFIG.pending;
          const history = selectedOrder.statusHistory || [];
          const histMap = {};
          history.forEach((h) => {
            histMap[h.status] = h;
          });
          const cancelEntry = history.find((h) => h.status === "cancelled");
          const isCancelled = selectedOrder.orderStatus === "cancelled";

          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={() => setSelectedOrder(null)}
              />
              <div className="relative bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto">
                {/* Modal header */}
                <div className="sticky top-0 bg-white rounded-t-3xl px-5 py-4 border-b border-gray-100 flex justify-between items-start z-10">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="font-extrabold text-lg">
                        #{selectedOrder.orderId}
                      </h2>
                      <span
                        className="text-xs font-bold px-3 py-1 rounded-full text-white"
                        style={{ background: cfg.color }}
                      >
                        {cfg.icon} {cfg.label}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Placed {fmtDate(selectedOrder.createdAt)}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedOrder(null)}
                    className="p-2 rounded-full hover:bg-gray-100 text-gray-400 text-lg leading-none"
                  >
                    ✕
                  </button>
                </div>

                <div className="p-5 flex flex-col gap-5">
                  {/* ── Cancellation reason banner ── */}
                  {isCancelled && cancelEntry && (
                    <div
                      style={{
                        background: "#fef2f2",
                        border: "1.5px solid #fecaca",
                        borderRadius: 14,
                        padding: "12px 16px",
                      }}
                    >
                      <p
                        style={{
                          fontSize: 11,
                          fontWeight: 800,
                          color: "#ef4444",
                          textTransform: "uppercase",
                          letterSpacing: "0.8px",
                          margin: "0 0 5px",
                        }}
                      >
                        ✕ Cancellation Reason
                      </p>
                      <p
                        style={{
                          fontSize: 13,
                          color: "#374151",
                          margin: "0 0 4px",
                          fontWeight: 600,
                        }}
                      >
                        "{cancelEntry.note || "No reason provided"}"
                      </p>
                      <p style={{ fontSize: 11, color: "#9ca3af", margin: 0 }}>
                        Cancelled on {fmtDate(cancelEntry.timestamp)}
                      </p>
                    </div>
                  )}

                  {/* ── Customer + Order info ── */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div
                      style={{
                        background: "#f9fafb",
                        borderRadius: 12,
                        padding: "12px 14px",
                        border: "1px solid #f0f0f0",
                      }}
                    >
                      <p
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: "#9ca3af",
                          textTransform: "uppercase",
                          letterSpacing: "0.8px",
                          margin: "0 0 8px",
                        }}
                      >
                        Customer
                      </p>
                      <p className="text-sm font-bold text-gray-800">
                        {selectedOrder.customerDetails?.name || "—"}
                      </p>
                      <p className="text-xs text-gray-400 font-mono mt-0.5">
                        {selectedOrder.customerDetails?.phone}
                      </p>
                      {selectedOrder.customerDetails?.email && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          {selectedOrder.customerDetails.email}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">
                        📍 {selectedOrder.customerDetails?.address?.street},{" "}
                        {selectedOrder.customerDetails?.address?.city} -{" "}
                        {selectedOrder.customerDetails?.address?.pincode}
                      </p>
                    </div>
                    <div
                      style={{
                        background: "#f9fafb",
                        borderRadius: 12,
                        padding: "12px 14px",
                        border: "1px solid #f0f0f0",
                      }}
                    >
                      <p
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: "#9ca3af",
                          textTransform: "uppercase",
                          letterSpacing: "0.8px",
                          margin: "0 0 8px",
                        }}
                      >
                        Order Info
                      </p>
                      <div className="flex flex-col gap-1.5">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Payment</span>
                          <span className="font-bold uppercase">
                            {selectedOrder.paymentMethod}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Subtotal</span>
                          <span className="font-semibold">
                            ₹{selectedOrder.subtotal || selectedOrder.total}
                          </span>
                        </div>
                        {selectedOrder.deliveryFee > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Delivery</span>
                            <span className="font-semibold">
                              ₹{selectedOrder.deliveryFee}
                            </span>
                          </div>
                        )}
                        <div
                          style={{
                            height: 1,
                            background: "#e5e7eb",
                            margin: "2px 0",
                          }}
                        />
                        <div className="flex justify-between">
                          <span className="text-sm font-bold text-gray-800">
                            Total
                          </span>
                          <span className="text-base font-black text-green-700">
                            ₹{selectedOrder.total}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ── Status timeline — collapsible ── */}
                  <div
                    style={{
                      background: "#f9fafb",
                      borderRadius: 12,
                      border: "1px solid #f0f0f0",
                      overflow: "hidden",
                    }}
                  >
                    <button
                      onClick={() => setShowHistory((h) => !h)}
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "12px 14px",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                      }}
                    >
                      <p
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: "#9ca3af",
                          textTransform: "uppercase",
                          letterSpacing: "0.8px",
                          margin: 0,
                        }}
                      >
                        Status History
                      </p>
                      <span
                        style={{
                          fontSize: 11,
                          color: "#9ca3af",
                          display: "inline-block",
                          transform: showHistory ? "rotate(180deg)" : "none",
                          transition: "transform 0.2s",
                        }}
                      >
                        ▾
                      </span>
                    </button>
                    {showHistory && (
                      <div style={{ padding: "0 14px 12px" }}>
                        <div
                          style={{ display: "flex", flexDirection: "column" }}
                        >
                          {(isCancelled
                            ? history
                            : [
                                {
                                  status: "pending",
                                  label: "Order Placed",
                                  icon: "🛒",
                                },
                                {
                                  status: "confirmed",
                                  label: "Confirmed",
                                  icon: "✅",
                                },
                                {
                                  status: "preparing",
                                  label: "Preparing",
                                  icon: "👨‍🍳",
                                },
                                {
                                  status: "shipped",
                                  label: "Shipped",
                                  icon: "📦",
                                },
                                {
                                  status: "out_for_delivery",
                                  label: "Out for Delivery",
                                  icon: "🚴",
                                },
                                {
                                  status: "delivered",
                                  label: "Delivered",
                                  icon: "🎉",
                                },
                              ]
                          ).map((step, i, arr) => {
                            // cancelled orders: use history entries directly
                            if (isCancelled) {
                              const hcfg =
                                STATUS_CONFIG[step.status] ||
                                STATUS_CONFIG.pending;
                              const isLast = i === arr.length - 1;
                              return (
                                <div
                                  key={i}
                                  style={{ display: "flex", gap: 10 }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      flexShrink: 0,
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: 26,
                                        height: 26,
                                        borderRadius: "50%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: 12,
                                        background: hcfg.bg,
                                        border: `2px solid ${hcfg.border}`,
                                        color: hcfg.color,
                                        fontWeight: 900,
                                      }}
                                    >
                                      {hcfg.icon}
                                    </div>
                                    {!isLast && (
                                      <div
                                        style={{
                                          width: 2,
                                          flex: 1,
                                          minHeight: 16,
                                          background: "#e5e7eb",
                                          margin: "2px 0",
                                        }}
                                      />
                                    )}
                                  </div>
                                  <div
                                    style={{
                                      paddingBottom: isLast ? 0 : 10,
                                      paddingTop: 2,
                                    }}
                                  >
                                    <p
                                      style={{
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: hcfg.color,
                                        margin: 0,
                                      }}
                                    >
                                      {hcfg.label}
                                    </p>
                                    <p
                                      style={{
                                        fontSize: 11,
                                        color: "#9ca3af",
                                        margin: "1px 0 0",
                                      }}
                                    >
                                      {fmtDate(step.timestamp)}
                                    </p>
                                    {step.note &&
                                      step.note !== "Order placed" && (
                                        <p
                                          style={{
                                            fontSize: 11,
                                            color: "#6b7280",
                                            margin: "2px 0 0",
                                            fontStyle: "italic",
                                          }}
                                        >
                                          "{step.note}"
                                        </p>
                                      )}
                                  </div>
                                </div>
                              );
                            }

                            // active order: show all steps
                            const scfg =
                              STATUS_CONFIG[step.status] ||
                              STATUS_CONFIG.pending;
                            const h = histMap[step.status];
                            const isDone = !!h;
                            const isNow =
                              selectedOrder.orderStatus === step.status;
                            const isLast = i === arr.length - 1;
                            return (
                              <div
                                key={step.status}
                                style={{
                                  display: "flex",
                                  gap: 10,
                                  opacity: isDone ? 1 : 0.35,
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    flexShrink: 0,
                                  }}
                                >
                                  <div
                                    style={{
                                      width: 26,
                                      height: 26,
                                      borderRadius: "50%",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontSize: 12,
                                      background: isDone ? scfg.bg : "#f9fafb",
                                      border: `2px solid ${isDone ? scfg.border : "#e5e7eb"}`,
                                      color: isDone ? scfg.color : "#d1d5db",
                                      fontWeight: 900,
                                      boxShadow: isNow
                                        ? `0 0 0 3px ${scfg.color}22`
                                        : "none",
                                    }}
                                  >
                                    {isDone && !isNow ? "✓" : step.icon}
                                  </div>
                                  {!isLast && (
                                    <div
                                      style={{
                                        width: 2,
                                        flex: 1,
                                        minHeight: 16,
                                        background: isDone
                                          ? `${scfg.color}30`
                                          : "#e5e7eb",
                                        margin: "2px 0",
                                      }}
                                    />
                                  )}
                                </div>
                                <div
                                  style={{
                                    paddingBottom: isLast ? 0 : 10,
                                    paddingTop: 2,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 6,
                                    }}
                                  >
                                    <p
                                      style={{
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: isNow
                                          ? scfg.color
                                          : isDone
                                            ? "#374151"
                                            : "#d1d5db",
                                        margin: 0,
                                      }}
                                    >
                                      {step.label}
                                    </p>
                                    {isNow && (
                                      <span
                                        style={{
                                          fontSize: 9,
                                          fontWeight: 800,
                                          padding: "1px 6px",
                                          borderRadius: 20,
                                          background: `${scfg.color}15`,
                                          color: scfg.color,
                                        }}
                                      >
                                        NOW
                                      </span>
                                    )}
                                  </div>
                                  <p
                                    style={{
                                      fontSize: 11,
                                      color: isDone ? "#9ca3af" : "#d1d5db",
                                      margin: "1px 0 0",
                                    }}
                                  >
                                    {h ? fmtDate(h.timestamp) : "Pending"}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ── Order items — admin can strike off unavailable items ── */}
                  <div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: 10,
                        flexWrap: "wrap",
                        gap: 8,
                      }}
                    >
                      <p
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: "#9ca3af",
                          textTransform: "uppercase",
                          letterSpacing: "0.8px",
                          margin: 0,
                        }}
                      >
                        Order Items
                      </p>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span style={{ fontSize: 11, color: "#9ca3af" }}>
                          Click ✕ to remove unavailable items
                        </span>
                        {(() => {
                          // Check if removedItems differs from saved state
                          const savedRemoved = new Set(
                            (selectedOrder.items || [])
                              .map((it, i) => (it.removed ? i : -1))
                              .filter((i) => i >= 0),
                          );
                          const isDirty =
                            removedItems.size !== savedRemoved.size ||
                            [...removedItems].some((i) => !savedRemoved.has(i));
                          return isDirty ? (
                            <button
                              onClick={handleSaveItems}
                              disabled={savingItems}
                              style={{
                                padding: "5px 14px",
                                borderRadius: 8,
                                border: "none",
                                background:
                                  "linear-gradient(135deg,#ef4444,#dc2626)",
                                color: "#fff",
                                fontSize: 12,
                                fontWeight: 700,
                                cursor: "pointer",
                                opacity: savingItems ? 0.6 : 1,
                              }}
                            >
                              {savingItems ? "Saving…" : "💾 Update Bill"}
                            </button>
                          ) : null;
                        })()}
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      {selectedOrder.items?.map((item, i) => {
                        const isRemoved = removedItems.has(i);
                        return (
                          <div
                            key={i}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                              padding: "8px 10px",
                              background: isRemoved ? "#fef2f2" : "#f9fafb",
                              borderRadius: 10,
                              border: `1px solid ${isRemoved ? "#fecaca" : "#f0f0f0"}`,
                              transition: "all 0.15s",
                              opacity: isRemoved ? 0.7 : 1,
                            }}
                          >
                            <img
                              src={item.image}
                              alt={item.name}
                              style={{
                                width: 42,
                                height: 42,
                                borderRadius: 8,
                                objectFit: "cover",
                                flexShrink: 0,
                                filter: isRemoved ? "grayscale(1)" : "none",
                              }}
                              onError={(e) => {
                                e.target.src =
                                  "https://via.placeholder.com/42/e8f5e9/2D8B4E?text=🥤";
                              }}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <p
                                style={{
                                  fontSize: 13,
                                  fontWeight: 700,
                                  color: isRemoved ? "#9ca3af" : "#111827",
                                  margin: 0,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  textDecoration: isRemoved
                                    ? "line-through"
                                    : "none",
                                }}
                              >
                                {item.name}
                              </p>
                              <p
                                style={{
                                  fontSize: 11,
                                  color: "#9ca3af",
                                  margin: "2px 0 0",
                                  textDecoration: isRemoved
                                    ? "line-through"
                                    : "none",
                                }}
                              >
                                ₹{item.price} × {item.quantity}
                              </p>
                            </div>
                            <span
                              style={{
                                fontSize: 13,
                                fontWeight: 900,
                                color: isRemoved ? "#9ca3af" : "#15803d",
                                textDecoration: isRemoved
                                  ? "line-through"
                                  : "none",
                                flexShrink: 0,
                              }}
                            >
                              ₹{item.price * item.quantity}
                            </span>
                            <button
                              onClick={() =>
                                setRemovedItems((prev) => {
                                  const next = new Set(prev);
                                  next.has(i) ? next.delete(i) : next.add(i);
                                  return next;
                                })
                              }
                              title={
                                isRemoved
                                  ? "Restore item"
                                  : "Mark as unavailable"
                              }
                              style={{
                                width: 26,
                                height: 26,
                                borderRadius: "50%",
                                border: "none",
                                flexShrink: 0,
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 12,
                                fontWeight: 900,
                                background: isRemoved ? "#fee2e2" : "#f3f4f6",
                                color: isRemoved ? "#ef4444" : "#9ca3af",
                                transition: "all 0.15s",
                              }}
                            >
                              {isRemoved ? "↩" : "✕"}
                            </button>
                          </div>
                        );
                      })}
                    </div>

                    {/* Live bill preview when items are struck */}
                    {removedItems.size > 0 &&
                      (() => {
                        const THRESHOLD = 299,
                          FEE = 40;
                        const newSubtotal = (selectedOrder.items || [])
                          .filter((_, i) => !removedItems.has(i))
                          .reduce((s, it) => s + it.price * it.quantity, 0);
                        const newDelivery =
                          newSubtotal === 0
                            ? 0
                            : newSubtotal >= THRESHOLD
                              ? 0
                              : FEE;
                        const newTotal = newSubtotal + newDelivery;
                        const saving = selectedOrder.total - newTotal;
                        return (
                          <div
                            style={{
                              marginTop: 10,
                              padding: "10px 14px",
                              background: "rgba(239,68,68,0.05)",
                              borderRadius: 10,
                              border: "1px solid rgba(239,68,68,0.15)",
                            }}
                          >
                            <p
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: "#ef4444",
                                textTransform: "uppercase",
                                letterSpacing: "0.6px",
                                margin: "0 0 6px",
                              }}
                            >
                              Updated Bill Preview
                            </p>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 3,
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  fontSize: 12,
                                }}
                              >
                                <span style={{ color: "#6b7280" }}>
                                  New Subtotal
                                </span>
                                <span style={{ fontWeight: 600 }}>
                                  ₹{newSubtotal}
                                </span>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  fontSize: 12,
                                }}
                              >
                                <span style={{ color: "#6b7280" }}>
                                  Delivery Fee
                                </span>
                                <span style={{ fontWeight: 600 }}>
                                  {newDelivery === 0
                                    ? "Free"
                                    : `₹${newDelivery}`}
                                </span>
                              </div>
                              <div
                                style={{
                                  height: 1,
                                  background: "rgba(239,68,68,0.15)",
                                  margin: "3px 0",
                                }}
                              />
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 13,
                                    fontWeight: 800,
                                    color: "#111827",
                                  }}
                                >
                                  New Total
                                </span>
                                <div style={{ textAlign: "right" }}>
                                  <span
                                    style={{
                                      fontSize: 15,
                                      fontWeight: 900,
                                      color: "#15803d",
                                    }}
                                  >
                                    ₹{newTotal}
                                  </span>
                                  {saving > 0 && (
                                    <span
                                      style={{
                                        marginLeft: 8,
                                        fontSize: 11,
                                        fontWeight: 700,
                                        color: "#ef4444",
                                      }}
                                    >
                                      -₹{saving} refund
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                  </div>

                  {/* ── Admin note to customer ── */}
                  <div
                    style={{
                      borderRadius: 12,
                      border: "1.5px solid rgba(245,158,11,0.3)",
                      background: "rgba(245,158,11,0.04)",
                      padding: "12px 14px",
                    }}
                  >
                    <p
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: "#d97706",
                        textTransform: "uppercase",
                        letterSpacing: "0.8px",
                        margin: "0 0 8px",
                      }}
                    >
                      📢 Message to Customer
                    </p>
                    <p
                      style={{
                        fontSize: 11,
                        color: "#9ca3af",
                        margin: "0 0 8px",
                      }}
                    >
                      This message will appear on the customer&apos;s order
                      history page for this order.
                    </p>
                    <textarea
                      value={adminNote}
                      onChange={(e) => setAdminNote(e.target.value)}
                      placeholder="e.g. Your Mango Juice has been substituted with Orange Juice as it's currently out of stock. Sorry for the inconvenience!"
                      rows={3}
                      style={{
                        width: "100%",
                        boxSizing: "border-box",
                        padding: "9px 12px",
                        borderRadius: 10,
                        border: "1.5px solid rgba(245,158,11,0.25)",
                        background: "#fff",
                        fontSize: 13,
                        color: "#111827",
                        outline: "none",
                        fontFamily: "inherit",
                        resize: "vertical",
                        marginBottom: 8,
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#f59e0b";
                        e.target.style.boxShadow =
                          "0 0 0 3px rgba(245,158,11,0.1)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "rgba(245,158,11,0.25)";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 8,
                      }}
                    >
                      <span style={{ fontSize: 11, color: "#9ca3af" }}>
                        {selectedOrder.adminNote
                          ? "✓ Message currently set"
                          : "No message set yet"}
                      </span>
                      <div style={{ display: "flex", gap: 8 }}>
                        {adminNote && (
                          <button
                            onClick={() => {
                              setAdminNote("");
                            }}
                            style={{
                              padding: "6px 14px",
                              borderRadius: 8,
                              border: "1.5px solid #e5e7eb",
                              background: "#fff",
                              fontSize: 12,
                              fontWeight: 600,
                              color: "#6b7280",
                              cursor: "pointer",
                            }}
                          >
                            Clear
                          </button>
                        )}
                        <button
                          onClick={handleSaveNote}
                          disabled={
                            savingNote ||
                            adminNote === (selectedOrder.adminNote || "")
                          }
                          style={{
                            padding: "6px 16px",
                            borderRadius: 8,
                            border: "none",
                            background:
                              "linear-gradient(135deg,#d97706,#f59e0b)",
                            color: "#fff",
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: "pointer",
                            opacity:
                              savingNote ||
                              adminNote === (selectedOrder.adminNote || "")
                                ? 0.5
                                : 1,
                            transition: "opacity 0.15s",
                          }}
                        >
                          {savingNote ? "Saving…" : "💾 Save Note"}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* ── Update status ── */}
                  {!isCancelled &&
                    selectedOrder.orderStatus !== "delivered" && (
                      <div
                        style={{
                          background: "rgba(45,139,78,0.04)",
                          borderRadius: 12,
                          padding: "12px 14px",
                          border: "1px solid rgba(45,139,78,0.12)",
                        }}
                      >
                        <p
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: "#9ca3af",
                            textTransform: "uppercase",
                            letterSpacing: "0.8px",
                            margin: "0 0 10px",
                          }}
                        >
                          Update Status
                        </p>
                        <div
                          style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
                        >
                          {getNextStatuses(selectedOrder.orderStatus).map(
                            (status) => {
                              const scfg = STATUS_CONFIG[status];
                              const isCancel = status === "cancelled";
                              return (
                                <button
                                  key={status}
                                  onClick={() =>
                                    handleUpdateStatus(
                                      selectedOrder._id,
                                      status,
                                    )
                                  }
                                  disabled={updating}
                                  style={{
                                    padding: "8px 18px",
                                    borderRadius: 100,
                                    fontSize: 13,
                                    fontWeight: 700,
                                    border: isCancel
                                      ? "2px solid #fca5a5"
                                      : "none",
                                    background: isCancel
                                      ? "transparent"
                                      : scfg.color,
                                    color: isCancel ? "#ef4444" : "#fff",
                                    cursor: updating
                                      ? "not-allowed"
                                      : "pointer",
                                    opacity: updating ? 0.6 : 1,
                                    transition: "all 0.15s",
                                  }}
                                >
                                  {updating ? "⏳" : isCancel ? "✕" : "→"}{" "}
                                  {scfg.label}
                                </button>
                              );
                            },
                          )}
                        </div>
                      </div>
                    )}
                </div>
              </div>
            </div>
          );
        })()}
    </div>
  );
}
